/**
 * Backend `err_*` codes → user-facing copy for the scheduling wizard.
 *
 * Keep this map scoped to scheduling errors; cross-feature codes
 * (e.g. generic auth, calendar integration) stay in their own
 * feature's map. Entries are grouped by context (connection, details,
 * time/slots, interviewers, location, plan refs, note-taker,
 * HackerRank) — keep new codes in the matching section.
 *
 * Covers every code the legacy `utils/errors/Modules/schedule.js`
 * surfaced, so the new wizard never shows a raw `err_*` string.
 *
 * Usage:
 *   import { resolveScheduleErrorMessage } from '@/components/shared/interview-manager/ScheduleInterview/config/errorCopy';
 *   toast({ title: resolveScheduleErrorMessage(err), status: 'error' });
 */
import { ApiError, resolveErrorMessage } from '@/api/request';
import { getSenderErrorMessage, SENDER_ERROR_CODES } from './senderErrorCopy';

/**
 * Unusable-sender codes, expanded to copy. The toast only has the code, so the
 * sender's name degrades to "This sender" — the inline field error on the
 * Invites & emails step carries the real name.
 */
const SENDER_ERROR_COPY = Object.fromEntries(
  Object.entries(SENDER_ERROR_CODES).map(([code, { reason, useCase }]) => [
    code,
    getSenderErrorMessage({ reason, useCase }),
  ])
);

export const SCHEDULE_ERROR_COPY: Record<string, string> = {
  // ── Calendar & email connection ───────────────────────────────────
  err_no_cal_connected: 'Connect your calendar in Settings before scheduling.',
  // Sender mailbox not connected (raised when the picked From user has
  // no mailbox linked to Kula). Title-only toasts — no description, so each
  // string has to name the fix on its own (ENG-3916: the old copy stated the
  // condition and left the recruiter with no next step).
  err_sender_mailbox_not_connected:
    'The sender no longer has a connected mailbox. Pick a different From address.',
  err_candidate_email_sender_mailbox_not_connected:
    'The sender for the candidate email no longer has a connected mailbox. Pick a different From address.',
  err_interviewer_email_sender_mailbox_not_connected:
    'The sender for the interviewer email no longer has a connected mailbox. Pick a different From address.',
  // No longer raised by `core` (split into the per-block codes spread in
  // below), but kept so an in-flight client on an older deploy still maps it.
  err_email_delegation_disabled:
    "You can't send on behalf of this user — they've turned off email delegation. Pick a different From address.",
  ...SENDER_ERROR_COPY,
  hackerrank_account_not_connected:
    'HackerRank account not connected. Please connect your account.',

  // ── Calendar visibility (shared Outlook) ──────────────────────────
  // Microsoft Graph forbids a private-sensitivity event on a calendar
  // shared *with* you (delegate write access covers non-private events
  // only). The FE already locks the Private toggle OFF for non-owned
  // Outlook calendars, so this is a backstop for the load-race / stale
  // edit. Raised on `:base` → surfaces via `errors[0]`.
  err_private_interview_on_shared_calendar:
    'Only owners can mark events as private on Outlook shared calendars. Try again with a different calendar.',
  // ── Interview details (required fields) ───────────────────────────
  err_name_missing: 'Enter an interview name.',
  err_name_too_long: 'Interview name is too long.',
  err_kind_missing: 'Select an interview type.',
  err_interview_type_invalid: 'Select a valid interview type.',
  err_missing_location: 'Select an interview location.',
  err_interview_location_invalid: 'Select a valid interview location.',
  err_invalid_timezone:
    'The selected timezone is invalid. Please pick another.',
  err_source_missing: 'Interview source is missing — refresh and retry.',
  err_contact_email_missing:
    "The candidate's email is missing. Add it before scheduling.",
  err_interview_duration_invalid:
    'Duration must be between 1 and 1440 minutes.',

  // ── Time & slots ──────────────────────────────────────────────────
  err_invalid_time_range: 'Pick an end time that is after the start time.',
  err_invalid_week_start_date: 'The week must start on a Sunday.',
  err_time_parsing: 'We could not read that date. Please try another.',
  err_start_time_missing: 'Select a start time.',
  err_end_time_missing: 'Select an end time.',
  err_start_time_less_than_current_time:
    'Booked event is in the past, please fix',
  err_end_time_more_than_current_time:
    'The interview end time is in the past. Pick a future time.',
  err_end_time_less_than_start_time: 'End time must be after the start time.',
  err_end_time_less_than_or_eq_to_start_time:
    'End time must be after the start time.',
  err_slot_taken_already:
    'That time slot is already taken. Please choose a different time.',
  err_slots_overlapping:
    'That time slot is already taken. Please choose a different time.',
  err_slots_missing: 'Please add at least one available time slot.',
  err_slot_in_notice_time:
    'At least one selected time slot falls within the minimum notice period. Try again with different slots.',
  err_contact_duplicate_interview_for_same_time:
    'An interview has already been scheduled for this contact at the selected time. Please choose a different time.',

  // ── Interviewers, pool & host ─────────────────────────────────────
  err_participant_ids_required: 'Add at least one interviewer.',
  err_interviewers_list_or_pool_not_configured:
    'Add at least one interviewer to the interview plan first.',
  err_interview_pool_missing: 'Select an interviewer pool.',
  err_interview_pool_id_required: 'Select an interviewer pool.',
  err_both_interview_pool_and_manual_selection_cannot_be_true:
    "Interviewer pool and manual selection can't both be enabled.",
  err_either_manual_selection_or_interview_pool_required:
    'Add interviewers manually or select an interviewer pool.',
  err_host_id_invalid: 'The host is invalid, does not have zoom access',
  err_duplicate_active_schedule_ats_job_stage_action:
    'Self-schedule link for this interview has been sent to the candidate already',

  // ── Office & on-site location ─────────────────────────────────────
  err_ats_office_missing: 'Select an office for the interview.',
  err_onsite_location_missing: 'Add the on-site location.',
  err_interview_office_id_required:
    'Select an office for the onsite interview.',
  err_interview_office_not_found:
    'The selected office was not found. Please pick another.',

  // ── Meeting room ──────────────────────────────────────────────────
  err_duplicate_meeting_room_email_for_interview:
    'This meeting room is already booked for this interview.',
  // Room-availability fetch failures (trigger 422 + poll envelope `errors[0]`).
  // Reconnecting needs `manage_org_integrations`, which the scheduler usually
  // lacks — so direct them to ask an admin. `err_ms_auth_error` = the Microsoft
  // token died (integration disconnected); `err_no_meeting_room_integration`
  // = no integration set up.
  err_ms_auth_error:
    "Couldn't load meeting rooms. Ask your admin to reconnect the Microsoft integration.",
  err_no_meeting_room_integration:
    "Couldn't load meeting rooms. Ask your admin to set up the integration.",

  // ── Job plan / stage / application references ─────────────────────
  err_missing_job_stage_action:
    'This interview slot is missing from the job plan — refresh and retry.',
  err_invalid_interview_ats_job_stage_action:
    'This recommendation is only available for one-on-one interviews.',
  err_duplicate_interview_ats_job_stage_action:
    'Attempting to create an duplicate interview for a ATS job stage action',
  err_ats_job_stage_missing:
    'This interview is missing its job stage — refresh and retry.',
  err_ats_application_missing:
    'This candidate application is missing — refresh and retry.',
  err_ats_interview_not_found:
    'This interview no longer exists. Refresh and try again.',
  err_ats_schedule_not_found:
    'This schedule no longer exists. Refresh and try again.',

  // ── Note taker ────────────────────────────────────────────────────
  err_note_taker_mandatory:
    'Your admin requires an AI note-taker for interviews in this job.',

  // ── HackerRank interview lifecycle (legacy parity — verbatim) ─────
  hackerrank_interview_update_error:
    'Failed to update interview. Try again later.',
  hackerrank_interview_invite_failed:
    'Failed to schedule interview. Try again later.',
  unable_to_update_hackerrank_interview:
    'Failed to update interview as it was deleted on HackerRank.',
  hackerrank_interview_cancel_failed:
    'Failed to cancel interview. Try again later.',
  hackerrank_interview_delete_failed:
    'Failed to delete interview. Try again later.',
  unable_to_delete_hackerrank_interview:
    'Failed to cancel interview as it was deleted on HackerRank.',
};

export function resolveScheduleErrorMessage(
  err: unknown,
  fallback?: string
): string {
  // Map the BE `err_*` code wherever it landed. Depending on how the axios
  // interceptor reshapes a 4xx, the code can sit on `ApiError.code`, in
  // `ApiError.errors[0]`, or — for some shapes — only on the Error message
  // (a bare `err_slot_in_notice_time`). Earlier we keyed off `.code` alone,
  // so those other shapes fell through and surfaced the raw code in the
  // toast. Check every spot and map the first recognised code.
  // Order mirrors the old precedence: `err.code` first (unchanged for
  // every case that already mapped), then the primary `errors[0]` (which
  // `code` normally mirrors), then a bare Error message. We only ever map
  // an EXACT `SCHEDULE_ERROR_COPY` key, so a human-readable message can't
  // false-match — this strictly turns previously-raw codes into copy and
  // never alters a case the old resolver already handled.
  const candidates: string[] = [];
  if (err instanceof ApiError) {
    if (err.code) candidates.push(err.code);
    if (err.errors?.length) candidates.push(err.errors[0]);
  }
  if (err instanceof Error && err.message) candidates.push(err.message.trim());
  for (const code of candidates) {
    if (SCHEDULE_ERROR_COPY[code]) return SCHEDULE_ERROR_COPY[code];
  }
  return resolveErrorMessage(err, fallback);
}
